import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { adminAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const SKILL_FILTERS = [
  { label: 'Tất cả', value: 'all' },
  { label: 'Nghe', value: 'listening', icon: 'headphones' },
  { label: 'Đọc', value: 'reading', icon: 'import_contacts' },
  { label: 'Viết', value: 'writing', icon: 'edit_note' },
  { label: 'Nói', value: 'speaking', icon: 'record_voice_over' },
]

const LEVEL_FILTERS = ['Tất cả', 'A2', 'B1', 'B2', 'C1']

const SKILL_META: Record<string, { color: string; bg: string; icon: string }> = {
  listening: { color: '#006571', bg: '#e6fafb', icon: 'headphones' },
  reading:   { color: '#004be2', bg: '#e8eeff', icon: 'import_contacts' },
  writing:   { color: '#8e00d5', bg: '#f5e6ff', icon: 'edit_note' },
  speaking:  { color: '#b41340', bg: '#ffe8ed', icon: 'record_voice_over' },
}

const LEVEL_COLORS: Record<string, string> = {
  A2: '#22c55e', B1: '#004be2', B2: '#8e00d5', C1: '#b45309', C2: '#b41340',
}

const VSTEP_PART_LABELS: Record<string, string> = {
  part1: 'Part 1', part2: 'Part 2', part3: 'Part 3', part4: 'Part 4',
}

/** FR-005 — Real Content Library with VSTEP mapping, skill + level filters */
export default function LibraryPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [skillFilter, setSkillFilter] = useState('all')
  const [levelFilter, setLevelFilter] = useState('Tất cả')
  const [search, setSearch] = useState(searchParams.get('q') ?? '')
  const [page, setPage] = useState(1)

  // Sync search box when arriving via the global nav search (?q=...)
  useEffect(() => {
    const q = searchParams.get('q')
    if (q !== null) setSearch(q)
  }, [searchParams])

  const { data: contentItems, isLoading } = useQuery({
    queryKey: ['library-content', skillFilter, levelFilter, page],
    queryFn: () => adminAPI.listContent({
      skill: skillFilter !== 'all' ? skillFilter : undefined,
      page,
    }),
  })

  // Filter by level and search client-side (content already filtered by skill server-side)
  const filtered = (contentItems ?? []).filter((row: any[]) => {
    const [, , title, , cefrLevel, , , , , , transcript] = row
    if (levelFilter !== 'Tất cả' && cefrLevel !== levelFilter) return false
    if (search) {
      const q = search.toLowerCase()
      const titleStr = String(title ?? '').toLowerCase()
      const transcriptStr = String(transcript ?? '').toLowerCase()
      if (!titleStr.includes(q) && !transcriptStr.includes(q)) return false
    }
    return true
  })

  const getSkillRoute = (skill: string) => {
    const routes: Record<string, string> = {
      listening: '/listening', reading: '/reading', writing: '/writing', speaking: '/speaking',
    }
    return routes[skill] ?? '/session'
  }

  return (
    <AppLayout>
      {/* Hero header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>library_books</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Thư viện</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.75rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem', lineHeight: 1.1 }}>
          Rèn giũa <span style={{ color: '#004be2', fontStyle: 'italic' }}>thế mạnh.</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Nội dung thực từ TED, BBC, Guardian — được map vào đúng cấu trúc đề thi VSTEP.
        </p>
      </div>

      {/* Skill filters */}
      <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
        {SKILL_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => { setSkillFilter(f.value); setPage(1) }}
            style={{
              padding: '0.625rem 1.25rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem',
              background: skillFilter === f.value ? '#004be2' : 'white',
              color: skillFilter === f.value ? 'white' : '#535b71',
              boxShadow: skillFilter === f.value ? '0 8px 20px rgba(0,75,226,0.25)' : '0 4px 12px rgba(0,0,0,0.04)',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
            }}
          >
            {f.icon && <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>{f.icon}</span>}
            {f.label}
          </button>
        ))}
      </div>

      {/* Level filters + search */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {LEVEL_FILTERS.map(lvl => (
            <button
              key={lvl}
              onClick={() => { setLevelFilter(lvl); setPage(1) }}
              style={{
                padding: '0.375rem 0.875rem', borderRadius: '9999px', border: 'none', cursor: 'pointer',
                fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.75rem',
                background: levelFilter === lvl
                  ? (LEVEL_COLORS[lvl] ?? '#535b71')
                  : '#eef0ff',
                color: levelFilter === lvl ? 'white' : '#535b71',
                transition: 'all 0.2s',
              }}
            >
              {lvl}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative' }}>
          <span className="material-symbols-outlined" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#a5adc6', fontSize: '1.125rem' }}>search</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Tìm nội dung..."
            style={{
              paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem',
              borderRadius: '1.25rem', background: 'white', border: 'none', outline: 'none',
              fontFamily: "'Manrope', sans-serif", fontSize: '0.875rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.04)', width: '14rem',
            }}
          />
        </div>
      </div>

      {/* Content grid */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', background: 'white', borderRadius: '1.5rem' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>search_off</span>
          <H style={{ display: 'block', fontWeight: 700, color: '#535b71', marginBottom: '0.5rem' }}>Không tìm thấy nội dung phù hợp</H>
          <p style={{ color: '#a5adc6', fontSize: '0.875rem' }}>Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(17rem, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
          {filtered.map((row: any[], i: number) => {
            // DB row: [id, source, title, url, cefrLevel, skill, vstepParts, topicTags, difficultyScore, durationSeconds, wordCount, transcript, createdAt]
            const [id, source, title, url, cefrLevel, skill, vstepPartsRaw, topicTagsRaw, difficultyScore, durationSeconds, wordCount] = row
            const meta = SKILL_META[skill] ?? SKILL_META.reading
            const levelColor = LEVEL_COLORS[cefrLevel] ?? '#004be2'

            let vstepParts: string[] = []
            try { vstepParts = JSON.parse(vstepPartsRaw ?? '[]') } catch { vstepParts = [] }

            let topicTags: string[] = []
            try { topicTags = JSON.parse(topicTagsRaw ?? '[]') } catch { topicTags = [] }

            const durationLabel = durationSeconds
              ? `${Math.round(Number(durationSeconds) / 60)} phút`
              : wordCount
              ? `${wordCount} từ`
              : null

            return (
              <div
                key={id ?? i}
                style={{
                  background: 'white', borderRadius: '1.5rem', overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)', cursor: 'pointer', transition: 'all 0.3s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)' }}
                onClick={() => navigate(getSkillRoute(skill))}
              >
                {/* Card header */}
                <div style={{ height: '9rem', background: `linear-gradient(135deg,${meta.bg},${meta.bg}cc)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: '1rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '3.5rem', color: meta.color, opacity: 0.35 }}>{meta.icon}</span>
                  {/* Badges */}
                  <div style={{ position: 'absolute', top: '0.75rem', left: '0.75rem', display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                    <span style={{ padding: '0.2rem 0.5rem', borderRadius: '0.375rem', background: 'rgba(255,255,255,0.9)', fontSize: '0.625rem', fontWeight: 800, color: levelColor, letterSpacing: '0.06em' }}>
                      {cefrLevel}
                    </span>
                    {vstepParts.slice(0, 2).map((p: string) => (
                      <span key={p} style={{ padding: '0.2rem 0.5rem', borderRadius: '0.375rem', background: 'rgba(255,255,255,0.75)', fontSize: '0.625rem', fontWeight: 700, color: meta.color }}>
                        {VSTEP_PART_LABELS[p] ?? p}
                      </span>
                    ))}
                  </div>
                  {/* Source */}
                  {source && (
                    <div style={{ position: 'absolute', bottom: '0.75rem', right: '0.75rem', padding: '0.2rem 0.625rem', borderRadius: '0.375rem', background: 'rgba(255,255,255,0.85)', fontSize: '0.625rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {source}
                    </div>
                  )}
                  {/* Audio indicator */}
                  {url && skill === 'listening' && (
                    <div style={{ position: 'absolute', bottom: '0.75rem', left: '0.75rem', width: '1.75rem', height: '1.75rem', borderRadius: '50%', background: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '0.875rem', color: 'white', fontVariationSettings: "'FILL' 1" }}>play_arrow</span>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div style={{ padding: '1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem', fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: meta.color }}>
                    <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>{meta.icon}</span>
                    {skill}
                  </div>
                  <H style={{ display: 'block', fontWeight: 800, fontSize: '0.9375rem', marginBottom: '0.5rem', lineHeight: 1.3 }}>
                    {String(title ?? '').slice(0, 60)}{String(title ?? '').length > 60 ? '...' : ''}
                  </H>

                  {/* Topic tags */}
                  {topicTags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      {topicTags.slice(0, 3).map((tag: string) => (
                        <span key={tag} style={{ padding: '0.125rem 0.5rem', borderRadius: '9999px', background: '#eef0ff', color: '#535b71', fontSize: '0.6875rem', fontWeight: 600 }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#a5adc6', fontWeight: 700 }}>
                    {durationLabel && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>timer</span>
                        {durationLabel}
                      </span>
                    )}
                    {difficultyScore && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>signal_cellular_alt</span>
                        {Number(difficultyScore).toFixed(1)}/10
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && (contentItems ?? []).length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem' }}>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: 'none', background: page === 1 ? '#eef0ff' : 'white', color: page === 1 ? '#a5adc6' : '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: page === 1 ? 'not-allowed' : 'pointer', boxShadow: page === 1 ? 'none' : '0 4px 12px rgba(0,0,0,0.06)' }}
          >
            ← Trang trước
          </button>
          <span style={{ display: 'flex', alignItems: 'center', fontWeight: 700, color: '#535b71', fontSize: '0.875rem' }}>
            Trang {page}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: 'none', background: 'white', color: '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
          >
            Trang sau →
          </button>
        </div>
      )}
    </AppLayout>
  )
}
